import { useState } from 'react';

export default function CitasPage() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    fecha: '',
    hora: '',
    mensaje: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/public/form/submit/protecplus-citas', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          servicio: formData.servicio,
          fecha: formData.fecha,
          hora: formData.hora,
          mensaje: formData.mensaje
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          servicio: '',
          fecha: '',
          hora: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-3 cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/594dc4849304cbd3bd7a23edd8bf4d32/085c019ea1217b73a4a5e1e39cd86a89.jpeg"
                alt="PROTECPLUS INGENIERÍA"
                className="h-16 w-auto"
              />
            </a>
            <a 
              href="/"
              className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors cursor-pointer"
            >
              <i className="ri-home-line mr-2"></i>
              Volver al Inicio
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-6">
              <i className="ri-calendar-check-line text-white text-3xl"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Reserva tu Cita
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Solicita una visita técnica o consulta con nuestros expertos. Completa el formulario y nos pondremos en contacto contigo.
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form onSubmit={handleSubmit} data-readdy-form id="citas-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Nombre */}
                <div>
                  <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    placeholder="Tu nombre"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    placeholder="tu@email.com"
                  />
                </div>

                {/* Teléfono */}
                <div>
                  <label htmlFor="telefono" className="block text-sm font-semibold text-gray-700 mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    placeholder="600 123 456"
                  />
                </div>

                {/* Servicio */}
                <div>
                  <label htmlFor="servicio" className="block text-sm font-semibold text-gray-700 mb-2">
                    Servicio Solicitado *
                  </label>
                  <select
                    id="servicio"
                    name="servicio"
                    value={formData.servicio}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  >
                    <option value="">Selecciona un servicio</option>
                    <option value="proteccion-incendios">Protección contra Incendios</option>
                    <option value="gas-radon">Medición Gas Radón</option>
                    <option value="seguridad-industrial">Seguridad Industrial</option>
                    <option value="formacion">Formación Técnica</option>
                    <option value="consultoria">Consultoría</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                {/* Fecha */}
                <div>
                  <label htmlFor="fecha" className="block text-sm font-semibold text-gray-700 mb-2">
                    Fecha Preferida *
                  </label>
                  <input
                    type="date"
                    id="fecha"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Hora */}
                <div>
                  <label htmlFor="hora" className="block text-sm font-semibold text-gray-700 mb-2">
                    Hora Preferida *
                  </label>
                  <select
                    id="hora"
                    name="hora"
                    value={formData.hora}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                  >
                    <option value="">Selecciona una hora</option>
                    <option value="07:00">07:00</option>
                    <option value="08:00">08:00</option>
                    <option value="09:00">09:00</option>
                    <option value="10:00">10:00</option>
                    <option value="11:00">11:00</option>
                    <option value="12:00">12:00</option>
                    <option value="13:00">13:00</option>
                    <option value="14:00">14:00</option>
                  </select>
                </div>
              </div>

              {/* Mensaje */}
              <div className="mb-6">
                <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-700 mb-2">
                  Mensaje Adicional
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
                  placeholder="Cuéntanos más detalles sobre tu solicitud..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">Máximo 500 caracteres</p>
              </div>

              {/* Info Box */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-emerald-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="text-sm text-emerald-800 font-medium mb-1">
                      Horario de Atención
                    </p>
                    <p className="text-sm text-emerald-700">
                      Lunes a Viernes: 7:00 - 15:00
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <i className="ri-checkbox-circle-line text-green-600 text-2xl"></i>
                    <div>
                      <p className="text-green-800 font-medium">¡Solicitud enviada con éxito!</p>
                      <p className="text-green-700 text-sm">Nos pondremos en contacto contigo pronto.</p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                    <div>
                      <p className="text-red-800 font-medium">Error al enviar la solicitud</p>
                      <p className="text-red-700 text-sm">Por favor, inténtalo de nuevo o contáctanos directamente.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center space-x-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Enviando...</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center space-x-2">
                      <i className="ri-send-plane-line"></i>
                      <span>Enviar Solicitud</span>
                    </span>
                  )}
                </button>
                <a
                  href="/"
                  className="flex-1 bg-gray-100 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-200 transition-all text-center whitespace-nowrap"
                >
                  Volver al Inicio
                </a>
              </div>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Teléfono</h3>
              <p className="text-gray-600 text-sm">981 16 88 45</p>
              <p className="text-gray-600 text-sm">679 90 30 30</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
              <p className="text-gray-600 text-sm">info@protecplus.es</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Horario</h3>
              <p className="text-gray-600 text-sm">Lunes a Viernes</p>
              <p className="text-gray-600 text-sm">7:00 - 15:00</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}